import { EditorIssue } from "../useTemplateEditorIssues";
import { EditorSelectionGates } from "../useEditorSelectionGates";
import { NestedEditorSelection } from "./useNestedEditorSelection";
import { NestedEditorRowActions } from "./useNestedEditorRowActions";
import { NestedEditorIssueFocus } from "./useNestedEditorIssueFocus";
import { NestedEditorCommandIslandProps } from "./NestedEditorCommandIsland";
import { NestedEditorHost } from "./nestedRowRefusal";

interface UseNestedEditorFooterParams {
    selection: NestedEditorSelection;
    gates: EditorSelectionGates;
    host: NestedEditorHost;
    rowActions: NestedEditorRowActions;
    issues: EditorIssue[];
    issueFocus: NestedEditorIssueFocus;
    onAddGroup: () => void;
    onAddAccount: () => void;
    isAddModalOpen: boolean;
    isSaving: boolean;
    isSaveDisabled: boolean;
    saveBlockedReason: string | undefined;
    saveHotkeyDescription: string;
    showSaveActions: boolean;
    onSave: () => void;
    onSaveAndClose: () => void;
    isEditingDisabled: boolean;
}

export const useNestedEditorFooter = ({
    selection,
    gates,
    host,
    rowActions,
    issues,
    issueFocus,
    onAddGroup,
    onAddAccount,
    isAddModalOpen,
    isSaving,
    isSaveDisabled,
    saveBlockedReason,
    saveHotkeyDescription,
    showSaveActions,
    onSave,
    onSaveAndClose,
    isEditingDisabled,
}: UseNestedEditorFooterParams): {
    islandProps: NestedEditorCommandIslandProps;
} => {
    // Workspace Archive keeps the "anything to act on" half of the gate and
    // drops the blocked-account half: a blocked row refuses itself and the rest
    // of the selection still proceeds.
    const canBulkDisable =
        host === "workspace"
            ? gates.disablableSelectedAccountIds.length > 0
            : gates.canBulkDisable;

    const islandProps: NestedEditorCommandIslandProps = {
        selectedCount: selection.selectedVisibleCount,
        actionableCount: gates.selectedVisibleAccountIds.length,
        canBulkEnable: gates.canBulkEnable,
        canBulkDisable,
        canBulkDelete: rowActions.canBulkDelete,
        bulkArchiveBlockReason:
            host === "workspace" ? undefined : gates.bulkArchiveBlockReason,
        bulkDeleteBlockReason: gates.bulkDeleteBlockReason,
        issueCount: issues.length,
        currentIssueIndex: issueFocus.safeIssueIndex,
        currentIssueMessage: issues[issueFocus.safeIssueIndex]?.message,
        isSaving,
        isSaveDisabled,
        saveBlockedReason,
        saveHotkeyDescription,
        showSaveActions,
        onClearSelection: selection.clearSelection,
        onBulkEnable: rowActions.handleBulkEnable,
        onBulkDisable: rowActions.handleBulkDisable,
        onBulkDelete: rowActions.handleBulkDelete,
        onNavigateIssue: issueFocus.focusIssue,
        onAddGroup,
        onAddAccount,
        onSave,
        onSaveAndClose,
        isAddModalOpen,
        isEditingDisabled,
    };

    return { islandProps };
};
